import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Bandeau, Champ } from './Champs'
import { Bouton, Icone } from '../ui'
import { ErreurApi, connecter, demanderReinitialisation, inscrire, reinitialiserMotDePasse } from '../api'
import { TEXTES, messageErreur, type Langue } from '../i18n'

interface Props {
  langue: Langue
  onConnecte: () => void
  onNaviguer: (vue: Vue) => void
}

export type Vue = 'connexion' | 'inscription' | 'oubli'

/**
 * L'enveloppe commune aux trois écrans — tickets `fo-connexion-01`, `fo-inscription-01`,
 * `fo-oubli-01`.
 *
 * Les trois étaient restés en l'état de L1 : un `<h2>` nu, des champs à la file, un bouton, et
 * une ligne de liens. La refonte L10 ne les avait jamais traversés.
 *
 * **Une seule enveloppe pour les trois**, parce que la maquette leur donne exactement la même :
 * titre centré, sous-titre, formulaire dans un bloc. Écrite trois fois, elle aurait divergé au
 * premier ajustement — et c'est un écran sur lequel on arrive avant d'avoir un compte, donc le
 * premier que voit quelqu'un qui ne connaît pas encore le produit.
 *
 * **28 rem et non 44.** Les trois écrans portent une tâche unique, sans navigation : la maquette
 * les resserre au-delà de sa propre largeur de lecture, et un formulaire de trois champs étalé
 * sur 44 rem a l'air d'attendre autre chose.
 */
function EnveloppeAuth({
  titre,
  sousTitre,
  children,
}: {
  titre: string
  sousTitre: string
  children: ReactNode
}) {
  return (
    <section className="authentification">
      <div className="page-entete centre">
        <h1>{titre}</h1>
        <p>{sousTitre}</p>
      </div>

      {children}
    </section>
  )
}

/**
 * Les trois fournisseurs d'identité, **posés et désactivés** — ticket `fo-connexion-01`.
 *
 * Les applications OAuth n'existent pas encore (L1b). Les boutons sont là quand même, et c'est
 * une décision de la maquette : la place est RÉSERVÉE dans la mise en page, pas promise à
 * l'utilisateur. Les découvrir le jour de leur activation déplacerait tout ce qui les entoure ;
 * les montrer éteints dit ce qui va venir sans le proposer.
 *
 * Chacun porte la raison de son état en `title` — un bouton gris sans explication se lit comme
 * une panne. `aria-disabled` en plus de `disabled` : le second retire l'élément du parcours au
 * clavier, et quelqu'un qui navigue ainsi ne saurait jamais que l'option existe.
 */
function FournisseursExternes({ t }: { t: (typeof TEXTES)[Langue] }) {
  return (
    <>
      <p className="separateur-ou">
        <span>{t.authOu}</span>
      </p>

      <div className="pile pile--serree">
        {[t.authGoogle, t.authMicrosoft, t.authFacebook].map((libelle) => (
          <button
            key={libelle}
            type="button"
            className="bouton bouton--secondaire bouton--bloc"
            disabled
            aria-disabled="true"
            title={t.authFournisseurIndisponible}
          >
            <Icone nom="globe" taille="sm" />
            {libelle}
          </button>
        ))}

        <p className="tertiaire centre">{t.authFournisseurIndisponible}</p>
      </div>
    </>
  )
}

/**
 * Un seul champ de contact, « e-mail ou téléphone » (conception L1, §1.6).
 *
 * Le format tranche côté serveur : présence d'un `@` ⇒ e-mail, sinon normalisation E.164 avec
 * `MA` par défaut. Deux champs séparés obligeraient l'utilisateur à se rappeler avec quoi il s'est
 * inscrit — et donneraient deux messages d'erreur là où l'anti-énumération en exige un seul.
 */
export function Connexion({ langue, onConnecte, onNaviguer }: Props) {
  const t = TEXTES[langue]
  const [contact, setContact] = useState('')
  const [motDePasse, setMotDePasse] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const [enCours, setEnCours] = useState(false)

  async function soumettre(evenement: React.FormEvent) {
    evenement.preventDefault()
    setErreur(null)
    setEnCours(true)

    try {
      await connecter(contact, motDePasse)
      onConnecte()
    } catch (echec) {
      setErreur(
        echec instanceof ErreurApi
          ? messageErreur(langue, echec.code, echec.reessayerDans)
          : messageErreur(langue, 'internal'),
      )
    } finally {
      setEnCours(false)
    }
  }

  return (
    <EnveloppeAuth titre={t.seConnecter} sousTitre={t.authAccroche}>
      <form className="bloc formulaire" onSubmit={soumettre} noValidate>
        {erreur && <Bandeau ton="erreur">{erreur}</Bandeau>}

        <Champ
          id="contact"
          libelle={t.contact}
          aide={t.contactAide}
          valeur={contact}
          autoComplete="username"
          requis
          onChange={setContact}
        />
        <Champ
          id="motDePasse"
          libelle={t.motDePasse}
          type="password"
          valeur={motDePasse}
          autoComplete="current-password"
          requis
          onChange={setMotDePasse}
        />

        <Bouton type="submit" taille="lg" bloc charge={enCours}>
          {t.seConnecter}
        </Bouton>

        <div className="rangee rangee--espace">
          <button type="button" className="lien" onClick={() => onNaviguer('oubli')}>
            {t.motDePasseOublie}
          </button>
          <button type="button" className="lien" onClick={() => onNaviguer('inscription')}>
            {t.pasDeCompte}
          </button>
        </div>
      </form>

      <FournisseursExternes t={t} />
    </EnveloppeAuth>
  )
}

export function Inscription({ langue, onConnecte, onNaviguer }: Props) {
  const t = TEXTES[langue]
  const [contact, setContact] = useState('')
  const [motDePasse, setMotDePasse] = useState('')
  const [nomAffiche, setNomAffiche] = useState('')
  const [conditions, setConditions] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const [enCours, setEnCours] = useState(false)

  async function soumettre(evenement: React.FormEvent) {
    evenement.preventDefault()
    setErreur(null)
    setEnCours(true)

    try {
      await inscrire(contact, motDePasse, nomAffiche, langue)

      // TODO(D22) : le compte est immédiatement utilisable, sans confirmation. On enchaîne donc
      // sur la connexion plutôt que d'afficher un écran « vérifiez vos e-mails » qui mentirait —
      // rien n'est envoyé tant que `Identity:RequireVerifiedContact` vaut `false`.
      await connecter(contact, motDePasse)
      onConnecte()
    } catch (echec) {
      setErreur(
        echec instanceof ErreurApi
          ? messageErreur(langue, echec.code, echec.reessayerDans)
          : messageErreur(langue, 'internal'),
      )
    } finally {
      setEnCours(false)
    }
  }

  return (
    <EnveloppeAuth titre={t.sInscrire} sousTitre={t.authAccrocheInscription}>
      <form className="bloc formulaire" onSubmit={soumettre} noValidate>
        {erreur && <Bandeau ton="erreur">{erreur}</Bandeau>}

        {/* L'ordre de la maquette : le nom d'abord. On donne son nom avant son adresse — et
            « nom affiché » a besoin de son aide, qui répond à la question que tout le monde se
            pose à ce champ-là : est-ce que ce sera public ? */}
        <Champ
          id="nomAffiche"
          libelle={t.nomAffiche}
          aide={t.nomAfficheAide}
          valeur={nomAffiche}
          autoComplete="nickname"
          requis
          onChange={setNomAffiche}
        />
        <Champ
          id="contact"
          libelle={t.contact}
          aide={t.contactAide}
          valeur={contact}
          autoComplete="username"
          requis
          onChange={setContact}
        />
        <Champ
          id="motDePasse"
          libelle={t.motDePasse}
          type="password"
          aide={t.motDePasseAide}
          valeur={motDePasse}
          autoComplete="new-password"
          requis
          onChange={setMotDePasse}
        />

        {/*
          Les conditions se cochent, elles ne se subissent pas. Un consentement implicite
          « en créant un compte vous acceptez » n'en est pas un — loi 09-08.

          **Et elles se lisent** — ticket `fo-inscription-02`. Cocher « j'accepte » sous un texte
          qui ne mène nulle part est un consentement de façade : la loi 09-08 demande un
          consentement éclairé, et on ne s'éclaire pas sur un document qu'on ne peut pas ouvrir.

          Les liens s'ouvrent dans un onglet séparé. Naviguer sur place ferait perdre le nom,
          l'adresse et le mot de passe déjà saisis — et la conséquence pratique serait qu'on ne
          les ouvre jamais.

          Ils vivent DANS le `<label>` sans le déclencher : la spécification HTML exclut du
          comportement d'activation d'un label les événements visant un descendant interactif, et
          un `<a href>` en est un. Le clic ouvre donc le document sans cocher la case.
        */}
        <label className="case">
          <input
            type="checkbox"
            checked={conditions}
            onChange={(evenement) => setConditions(evenement.target.checked)}
          />
          <span>
            {t.authConditionsAvant}
            <Link to="/informations-legales#conditions" target="_blank" rel="noreferrer">
              {t.legalConditions}
            </Link>
            {t.authConditionsEntre}
            <Link to="/informations-legales#confidentialite" target="_blank" rel="noreferrer">
              {t.legalConfidentialite}
            </Link>
            {t.authConditionsFin}
          </span>
        </label>

        <Bouton type="submit" taille="lg" bloc charge={enCours} desactive={!conditions}>
          {t.authCreerMonCompte}
        </Bouton>

        <p className="centre">
          <button type="button" className="lien" onClick={() => onNaviguer('connexion')}>
            {t.dejaInscrit}
          </button>
        </p>
      </form>

      {/* D22 : le compte est utilisable immédiatement, et la vérification viendra. Le dire ici
          évite la question « pourquoi n'ai-je rien reçu ? », qui est la première du support. */}
      <Bandeau ton="info">{t.authVerificationDifferee}</Bandeau>

      <FournisseursExternes t={t} />
    </EnveloppeAuth>
  )
}

/**
 * Mot de passe oublié.
 *
 * Le message de confirmation est le MÊME que le compte existe ou non — c'est le pendant client de
 * la réponse constante du serveur. Afficher « compte introuvable » ici rendrait inutile tout le
 * soin pris côté serveur à égaliser corps et durée de réponse.
 */
export function Oubli({ langue, onNaviguer }: Props) {
  const t = TEXTES[langue]
  const [contact, setContact] = useState('')
  const [envoye, setEnvoye] = useState(false)
  const [enCours, setEnCours] = useState(false)

  async function soumettre(evenement: React.FormEvent) {
    evenement.preventDefault()
    setEnCours(true)

    try {
      await demanderReinitialisation(contact)
    } catch {
      // Même en cas d'échec réseau : afficher un message différent trahirait l'existence du
      // compte selon le moment de l'erreur.
    } finally {
      setEnvoye(true)
      setEnCours(false)
    }
  }

  return (
    <EnveloppeAuth titre={t.motDePasseOublie} sousTitre={t.authAccrocheOubli}>
      <form className="bloc formulaire" onSubmit={soumettre} noValidate>
        {envoye ? (
          <Bandeau ton="succes">{t.reinitialisationEnvoyee}</Bandeau>
        ) : (
          <>
            <Champ
              id="contact"
              libelle={t.contact}
              aide={t.contactAide}
              valeur={contact}
              autoComplete="username"
              requis
              onChange={setContact}
            />
            <Bouton type="submit" taille="lg" bloc charge={enCours}>
              {t.envoyer}
            </Bouton>
          </>
        )}

        <p className="centre">
          <button type="button" className="lien" onClick={() => onNaviguer('connexion')}>
            {t.authRevenirConnexion}
          </button>
        </p>
      </form>
    </EnveloppeAuth>
  )
}

/** Consommation du lien de réinitialisation, atteint par `/reinitialiser?token=…`. */
export function Reinitialisation({ langue, token }: { langue: Langue; token: string }) {
  const t = TEXTES[langue]
  const [motDePasse, setMotDePasse] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const [fait, setFait] = useState(false)
  const [enCours, setEnCours] = useState(false)

  async function soumettre(evenement: React.FormEvent) {
    evenement.preventDefault()
    setErreur(null)
    setEnCours(true)

    try {
      await reinitialiserMotDePasse(token, motDePasse)
      setFait(true)
    } catch (echec) {
      setErreur(
        echec instanceof ErreurApi
          ? messageErreur(langue, echec.code, echec.reessayerDans)
          : messageErreur(langue, 'internal'),
      )
    } finally {
      setEnCours(false)
    }
  }

  // Le quatrième écran d'authentification. Le testeur ne l'a pas ouvert — on n'y arrive que par
  // un lien reçu — mais le laisser en l'état ferait un écran sur quatre à ne pas ressembler aux
  // autres, et c'est celui sur lequel on atterrit depuis sa boîte mail.
  if (fait) {
    return (
      <EnveloppeAuth titre={t.reinitialiser} sousTitre={t.authAccrocheOubli}>
        <div className="bloc formulaire">
          <Bandeau ton="succes">{t.motDePasseChange}</Bandeau>
          <p className="centre">
            <a className="lien" href="/">
              {t.seConnecter}
            </a>
          </p>
        </div>
      </EnveloppeAuth>
    )
  }

  return (
    <EnveloppeAuth titre={t.reinitialiser} sousTitre={t.authAccrocheReinitialiser}>
      <form className="bloc formulaire" onSubmit={soumettre} noValidate>
        {erreur && <Bandeau ton="erreur">{erreur}</Bandeau>}

        <Champ
          id="nouveauMotDePasse"
          libelle={t.nouveauMotDePasse}
          type="password"
          aide={t.motDePasseAide}
          valeur={motDePasse}
          autoComplete="new-password"
          requis
          onChange={setMotDePasse}
        />

        <Bouton type="submit" taille="lg" bloc charge={enCours}>
          {t.reinitialiser}
        </Bouton>
      </form>
    </EnveloppeAuth>
  )
}
